<script setup lang="ts">
import 'lucide-static/font/lucide.css';

import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { extractJsonFromString } from 'extract-json-from-string-y';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useSessionStore } from '@/stores/SessionStore.ts';
import HotkeyIndicator from '@/components/hotkey/HotkeyIndicator.vue';
import { Hotkey } from '@/types/Hotkey.ts';
import Loader from '@/components/loader/Loader.vue';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import type { StepConfig } from '@/types/edit/StepConfig.ts';
import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import { getNeighbouringStepConfig } from '@/utils/PromptUtils.ts';
import { VariableService } from '@/services/VariableService.ts';
import { eventEmitter, type HotkeyPressPayload } from '@/lib/eventEmitter.ts';
import PromptStatusBadge from '@/components/output/PromptStatusBadge.vue';
import { router, ROUTES } from '@/router.ts';
import { PromptService } from '@/services/PromptService.ts';
import { useClientStore } from '@/stores/ClientStore.ts';
import { containsHtml } from '@/utils/RandomUtils.ts';
import { logger } from '@/lib/logger.ts';
import PromptEditButton from '@/components/output/PromptEditButton.vue';

interface ExtractedChoice {
    title: string;
    smallTitle: string;
    description: string;
}

const props = defineProps<{
    sessionId: string;
    profileConfig: ProfileConfig;
    stepConfig: StepConfig;
    promptConfig: PromptConfig;
    promptIndex: number;
    streamId: string;
    content: string;
    selectedNavigationIndex: number;
    variableChainHash: string;
    isPromptSlow: boolean;
}>();

const clientStore = useClientStore();
const sessionStore = useSessionStore();

const choices = ref<ExtractedChoice[]>([]);

const recalculateChoices = () => {
    const extractedChoices = extractJsonFromString(String(props.content));

    let extractedChoicesFlattened: ExtractedChoice[] = [];
    if (extractedChoices.length > 0) {
        if (Array.isArray(extractedChoices[0])) {
            extractedChoicesFlattened = [...extractedChoices[0]];
        } else {
            extractedChoicesFlattened = extractedChoices;
        }
    }

    const newOptions: Record<number, boolean> = {};
    for (let i = 0; i < extractedChoicesFlattened.length; i++) {
        newOptions[getIndex(i)] = true;
    }

    const navigationId = `${props.sessionId}_${props.stepConfig.id}`;

    if (clientStore.verticalNavigationOptions[navigationId] === undefined) {
        clientStore.verticalNavigationOptions[navigationId] = {};
    }

    clientStore.verticalNavigationOptions[navigationId] = {
        ...clientStore.verticalNavigationOptions[navigationId],
        ...newOptions,
    };

    choices.value = extractedChoicesFlattened;
};

const getIndex = (index: number) => {
    return index + props.promptIndex * 100;
};

const selectChoice = async (choice: ExtractedChoice) => {
    if (sessionStore.isInsideWebView) {
        return;
    }

    VariableService.setPromptUserInputVariable(
        props.sessionId,
        props.promptConfig.id,
        props.variableChainHash,
        choice.title,
    );

    const nextStepConfig = getNeighbouringStepConfig(
        props.profileConfig.id,
        props.stepConfig.id,
        'next',
    );

    if (!nextStepConfig) {
        logger.warn('No next step config found for profile config', props.profileConfig.id);
        return;
    }

    const nextPageVariableChainHash = VariableService.calculateVariableChainHash(
        props.sessionId,
        nextStepConfig.id,
    );

    void router.push({
        name: ROUTES.runIndex,
        params: {
            sessionId: props.sessionId,
            profileConfigId: props.profileConfig.id,
            stepConfigId: nextStepConfig.id,
            variableChainHash: nextPageVariableChainHash,
        },
    });
};

/* region Event handlers */
const onHotkeyPress = (payload: HotkeyPressPayload) => {
    const hotkey = payload.hotkey;

    if (hotkey === Hotkey.Right) {
        const offset = props.promptIndex * 100;
        const choice = choices.value[props.selectedNavigationIndex - offset];

        if (!choice) {
            return;
        }

        selectChoice(choice);
    }
};
/* endregion Event handlers */

/* region Computed */
const promptStatus = computed(() => {
    return PromptService.getPromptStatus(
        props.promptConfig.id,
        props.sessionId,
        props.variableChainHash,
    );
});
/* endregion Computed */

/* region Lifecycle */
watch(
    () => [props.content],
    async () => {
        recalculateChoices();
    },
);

onMounted(() => {
    eventEmitter.addListener('hotkeyPress', onHotkeyPress);

    recalculateChoices();
});

onUnmounted(() => {
    eventEmitter.removeListener('hotkeyPress', onHotkeyPress);
});
/* endregion Lifecycle */
</script>

<template>
    <Card class="mb-2">
        <CardHeader class="px-4 py-2 pe-2 text-lg">
            <CardTitle>
                <div class="flex items-center justify-between text-base">
                    {{ promptConfig.name }}
                    <div class="flex items-center gap-x-1">
                        <PromptStatusBadge
                            :prompt-config-id="promptConfig.id"
                            :session-id="sessionId"
                            :variable-chain-hash="variableChainHash"
                        />
                        <PromptEditButton
                            v-if="!sessionStore.isInsideWebView"
                            :profile-config-id="profileConfig.id"
                            :step-config-id="stepConfig.id"
                            :prompt-config-id="promptConfig.id"
                        />
                    </div>
                </div>
            </CardTitle>
        </CardHeader>
        <CardContent class="flex flex-col gap-y-2 p-2 dark:bg-zinc-900/60">
            <div
                v-if="promptStatus.status === 'error'"
                class="flex flex-col items-center justify-center gap-4 p-5"
            >
                Failed to retrieve output, please try again.
                <code v-if="content" class="max-h-48 max-w-md overflow-scroll rounded border p-2">
                    {{ content }}
                </code>
            </div>
            <div
                v-else-if="promptStatus.status === 'finished' && choices.length === 0"
                class="flex flex-col items-center justify-center gap-4 p-5"
            >
                <span class="text-center text-destructive">Failed to parse choice output:</span>
                <code class="max-h-48 max-w-md overflow-scroll rounded border p-2">
                    {{ content }}
                </code>
                <span class="text-center text-sm text-muted-foreground"
                    >Choice output should be a JSON array containing one or more objects with the
                    fields: "title", "description" and optionally "smallTitle".</span
                >
            </div>
            <Card
                v-else
                v-for="(choice, index) in choices"
                :key="index"
                :class="{
                    active: getIndex(index) === props.selectedNavigationIndex,
                    'hover:cursor-not-allowed': sessionStore.isInsideWebView,
                    'hover:border-gray-300': !sessionStore.isInsideWebView,
                }"
                class="select-none overflow-hidden rounded"
                :id="`choice-${getIndex(index)}`"
                @click="selectChoice(choice)"
            >
                <CardHeader class="p-4">
                    <CardTitle class="text-base">
                        <div
                            v-if="containsHtml(choice.title)"
                            v-html="choice.title"
                            class="flex"
                        ></div>
                        <span v-else>{{ choice.title }}</span>
                    </CardTitle>
                    <CardDescription class="mt-0" v-if="choice.smallTitle">
                        <div
                            v-if="containsHtml(choice.smallTitle)"
                            v-html="choice.smallTitle"
                            class="flex"
                        ></div>
                        <span v-else>{{ choice.smallTitle }}</span>
                    </CardDescription>
                </CardHeader>
                <CardContent class="flex items-center px-4 pb-4 text-sm">
                    <div class="flex w-full items-center justify-between">
                        <div class="flex items-center">
                            <div
                                v-if="containsHtml(choice.description)"
                                v-html="choice.description"
                                class="flex"
                            ></div>
                            <span v-else>{{ choice.description }}</span>
                        </div>
                        <div class="ms-2 w-[55px]">
                            <HotkeyIndicator
                                v-if="getIndex(index) === props.selectedNavigationIndex"
                                :hotkey="Hotkey.Right"
                                class="bg-secondary opacity-80"
                            />
                            <HotkeyIndicator
                                v-else-if="
                                    props.selectedNavigationIndex !== undefined &&
                                    getIndex(index) === props.selectedNavigationIndex + 1
                                "
                                :hotkey="Hotkey.Down"
                                class="bg-secondary/10"
                            />
                            <HotkeyIndicator
                                v-else-if="
                                    props.selectedNavigationIndex !== undefined &&
                                    getIndex(index) === props.selectedNavigationIndex - 1
                                "
                                :hotkey="Hotkey.Up"
                                class="bg-secondary/10 opacity-80"
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>
            <div
                v-if="
                    promptStatus.status === 'queued' ||
                    promptStatus.status === 'waiting' ||
                    promptStatus.status === 'streaming'
                "
                class="flex flex-col items-center justify-center p-5"
            >
                <Loader :width="24" :height="24" />
                <div
                    v-if="isPromptSlow && promptStatus.status !== 'streaming'"
                    class="mt-4 max-w-md text-center text-sm"
                >
                    <template v-if="!sessionStore.isInsideWebView">
                        The selected AI model (<strong>{{ promptConfig.modelId }}</strong
                        >) is currently taking a long time to respond. We recommend temporarily
                        <RouterLink
                            v-if="promptConfig.editable"
                            :to="{
                                name: ROUTES.editUpdate,
                                params: { profileConfigId: profileConfig.id },
                            }"
                            class="text-blue-600 hover:underline"
                            >switching</RouterLink
                        >
                        <RouterLink
                            v-else
                            :to="{
                                name: ROUTES.editIndex,
                            }"
                            class="text-blue-600 hover:underline"
                            >switching</RouterLink
                        >
                        to another model.
                    </template>
                    <template v-else>
                        The selected AI model (<strong>{{ promptConfig.modelId }}</strong
                        >) is currently taking a long time to respond. We recommend temporarily
                        switching to another model.
                    </template>
                </div>
            </div>
        </CardContent>
    </Card>
</template>

<style scoped>
.selected {
    @apply border border-green-600 bg-card/5;
}

.active {
    @apply border border-muted-foreground bg-card/5;
}
</style>
